import { IconButton, Menu, Tag } from '@neo4j-ndl/react';
import { EllipsisHorizontalIconOutline } from '@neo4j-ndl/react/icons';
import { ACTION, type IpFilter, usePermissions } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { DataGridHelpers, PermissionTooltip, SearchField } from '@nx/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo, useRef, useState } from 'react';

import { useCommonTable } from '../components/table/common-table';
import { DeleteIpFilterDialog } from './delete-dialog';
import { EditIpFilterSidebar } from './edit-sidebar';
import { IpFilteringScopeContext } from './ip-filter-scope';
import { IP_FILTERING_ACTION, IP_FILTERING_SCOPE } from './types';

type IpFilterAction = {
  action: IP_FILTERING_ACTION;
  ipFilter: IpFilter;
};

const ActionRow = ({
  ipFilter,
  setCurrentAction,
}: {
  ipFilter: IpFilter;
  setCurrentAction: (action: IpFilterAction | null) => void;
}) => {
  const { scope } = useContext(IpFilteringScopeContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { isAllowed } = usePermissions();
  const canEditFilter = isAllowed(ACTION.UPDATE, `organizations/${ipFilter.organizationId}/ip-filters/${ipFilter.id}`);
  const canDeleteFilter = isAllowed(
    ACTION.DELETE,
    `organizations/${ipFilter.organizationId}/ip-filters/${ipFilter.id}`,
  );
  const handleClick = () => setIsMenuOpen((prevState) => !prevState);

  return (
    <>
      <div className="flex w-full justify-end">
        <IconButton ref={menuRef} ariaLabel="More data source actions" onClick={handleClick} size="small" isClean>
          <EllipsisHorizontalIconOutline className="h-full w-full" />
        </IconButton>
      </div>
      <Menu isOpen={isMenuOpen} anchorRef={menuRef} onClose={handleClick}>
        <Menu.Items>
          <PermissionTooltip hasPermission={canEditFilter} placement="left" className="flex">
            <div className="w-full">
              <Menu.Item
                title="Inspect"
                isDisabled={!canEditFilter}
                onClick={() => setCurrentAction({ action: IP_FILTERING_ACTION.UPDATE, ipFilter: ipFilter })}
                className="w-full"
              />
            </div>
          </PermissionTooltip>
          {scope === IP_FILTERING_SCOPE.ORGANIZATION && (
            <PermissionTooltip hasButtonWrapper hasPermission={canDeleteFilter} placement="left">
              <Menu.Item
                title="Delete"
                isDisabled={!canDeleteFilter}
                onClick={() => setCurrentAction({ action: IP_FILTERING_ACTION.DELETE, ipFilter: ipFilter })}
                className="text-palette-danger-text w-full"
              />
            </PermissionTooltip>
          )}
        </Menu.Items>
      </Menu>
    </>
  );
};

const columnHelper = createColumnHelper<IpFilter>();

type IpFilterTableProps = {
  ipFilters: IpFilter[];
  isLoading: boolean;
};

export const IpFilteringTable = ({ ipFilters, isLoading }: IpFilterTableProps) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [currentAction, setCurrentAction] = useState<IpFilterAction | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        size: 100,
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (cx) => (isNotNullish(cx.getValue()) ? cx.getValue() : '-'),
        size: 250,
        maxSize: 400,
      }),
      columnHelper.accessor('allowList', {
        header: 'Allow List',
        cell: (cx) => {
          const simplifiedAllowList = cx.getValue().map((cidrObject) => {
            let cidr = cidrObject.address;
            if (cidrObject.prefixLen !== 32) {
              cidr = cidr.concat(`/${cidrObject.prefixLen}`);
            }
            return cidr;
          });
          return simplifiedAllowList.length > 0 ? (
            <div className="overflow-scroll whitespace-nowrap">
              {simplifiedAllowList.map((cidr, index) => (
                <Tag key={index} type="default" className="mr-1 inline-block">
                  {cidr}
                </Tag>
              ))}
            </div>
          ) : (
            '-'
          );
        },
        size: 250,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (cx) => {
          const ipFilter = cx.row.original;

          return <ActionRow ipFilter={ipFilter} setCurrentAction={setCurrentAction} />;
        },
        size: 50,
        minSize: 50,
        maxSize: 50,
        enableResizing: false,
      }),
    ],
    [],
  );

  const table = useCommonTable({
    columns,
    data: ipFilters,
    initialState: {
      pagination: { pageSize: 25 },
    },
    enableColumnPinning: true,
    state: {
      columnPinning: {
        right: ['actions'],
      },
    },
  });

  return (
    <>
      <DataGridHelpers.Wrapper className="border-palette-neutral-border-weak border">
        <DataGridHelpers.OuterHeader>
          <div className="flex w-full flex-wrap justify-between gap-2">
            <div className="flex basis-[400px] gap-2">
              <SearchField
                className="min-w-36 grow"
                value={globalFilter}
                onChange={(event) => setGlobalFilter(event.target.value)}
                htmlAttributes={{
                  'aria-label': 'Search for custom endpoint',
                }}
              />
            </div>
          </div>
        </DataGridHelpers.OuterHeader>
        <DataGridHelpers.DataGridRightColumnPinned<IpFilter>
          isLoading={isLoading}
          tableInstance={table}
          styling={{
            headerStyle: 'clean',
            borderStyle: 'horizontal',
          }}
        />
      </DataGridHelpers.Wrapper>
      {currentAction?.action === IP_FILTERING_ACTION.UPDATE && (
        <EditIpFilterSidebar ipFilter={currentAction.ipFilter} onClose={() => setCurrentAction(null)} />
      )}
      {currentAction?.action === IP_FILTERING_ACTION.DELETE && (
        <DeleteIpFilterDialog ipFilter={currentAction.ipFilter} onClose={() => setCurrentAction(null)} />
      )}
    </>
  );
};
